import React, { useEffect, useRef } from 'react';

interface TextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  readOnly?: boolean;
}

const TextEditor: React.FC<TextEditorProps> = ({
  value,
  onChange,
  placeholder = "Commencez à écrire...",
  height = "400px",
  readOnly = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  // Handle input changes and formatting
  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const content = e.currentTarget.innerText;
    
    // Format the text with proper spacing and bold sections
    let formattedText = content
      // Split into sections (emoji + text)
      .split(/(?=[\u{1F300}-\u{1F9FF}])/gu)
      .map(section => {
        // If section starts with emoji, it's a title
        if (/^[\u{1F300}-\u{1F9FF}]/u.test(section)) {
          const lines = section.split('\n');
          // Bold the title line
          return `**${lines[0].trim()}**\n${lines.slice(1).join('\n')}`;
        }
        return section;
      })
      .join('\n\n\n') // Add extra line between sections
      // Ensure proper spacing between sections
      .replace(/\n{4,}/g, '\n\n\n')
      .trim();

    onChange(formattedText);
  };

  // Update editor content when value changes
  useEffect(() => {
    if (editorRef.current) {
      // Convert markdown bold to HTML
      const formattedHtml = value
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>');
      
      editorRef.current.innerHTML = formattedHtml || '<br>';
    }
  }, [value]);

  return (
    <div className="text-editor-wrapper" style={{ height }}>
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        onInput={handleInput}
        className="text-editor"
        data-placeholder={placeholder}
      />
      <style>{`
        .text-editor-wrapper {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          background: ${readOnly ? '#f9fafb' : '#ffffff'};
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .text-editor {
          flex: 1;
          width: 100%;
          height: 100%;
          padding: 1.5rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 16px;
          line-height: 1.8;
          color: #374151;
          background: transparent;
          border: none;
          resize: none;
          overflow-y: auto;
          cursor: text;
        }

        .text-editor:focus {
          outline: none;
        }

        .text-editor:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          font-style: italic;
        }

        .text-editor strong {
          font-weight: 800;
          font-size: 1.1em;
          color: #111827;
          display: block;
          margin-bottom: 0.5rem;
          letter-spacing: -0.01em;
          background: linear-gradient(to right, #1a1a1a, #333333);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .text-editor br {
          display: block;
          content: "";
          margin-top: 1rem;
        }

        /* Add more space between sections */
        .text-editor strong + br + br {
          margin-top: 2rem;
        }

        /* Webkit scrollbar */
        .text-editor::-webkit-scrollbar {
          width: 12px;
          background-color: #f5f5f5;
          display: block !important;
        }

        .text-editor::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }

        .text-editor::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 6px;
          border: 3px solid #f1f1f1;
        }

        .text-editor::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }

        /* Firefox scrollbar */
        .text-editor {
          scrollbar-width: thin;
          scrollbar-color: #c1c1c1 #f1f1f1;
        }
      `}</style>
    </div>
  );
};

export default TextEditor;